import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  BarChart3,
  CheckCircle,
  CircleCheckBig,
  Clock,
  List,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import AccountCheckInCard from "./AccountCheckInCard";
import CheckInModal from "./CheckInModal";
import CheckInPollAccordion from "./CheckInPollAccordion";
import {
  MCI_SAMPLE_MESSAGES,
  SampleMyCheckInsAssigned,
  SampleMyCheckInsCompleted,
} from "./MyCheckInsSampleState";
import MyCheckInsPerformancePanel from "./PerformancePanel";
import {
  accountHasPoll1Draft,
  draftFromAccount,
  hasPartialPoll1Answers,
  type Poll1Draft,
} from "./poll1DraftStorage";
import {
  buildSubmitEvidence,
  poll1RawFromAnswers,
  type Poll1Question,
  type Poll1Tier,
} from "./poll1Helpers";
import { accountsForPollTab, pollPrimaryTab } from "./pollTabHelpers";
import ScoreRevealPanel from "./ScoreRevealPanel";
import EngagementEmptyPanel, {
  HubPageLoading,
} from "../../Components/EngagementEmptyPanel";
import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import DiscoveryHubPollSectionAccordion from "../../Container/DiscoveryHub/DiscoveryHubPollSectionAccordion";
import { SampleDataOverlay } from "../../Container/DiscoveryHub/DiscoveryHubSampleState";
import { groupPollGroupsByPeriod } from "../../Container/DiscoveryHub/pollAccordionHelpers";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchPoll1Draft,
  fetchSponsorEngagementMyPerformance,
  fetchSponsorEngagementPortfolio,
  savePoll1Draft,
  submitPoll1,
} from "../../Store/Reducers/SponsorEngagementSlice";
import { formatQuarterPeriodLabel } from "../../Utils/Helper/sponsorEngagementUi";
import { notificationBannerClass } from "../../Utils/Helper/sponsorEngagementUi";
import { toast } from "../../Utils/Helper/toast";

export type CheckInTab = "assigned" | "completed" | "performance";

const TAB_IDS: CheckInTab[] = ["assigned", "completed", "performance"];

const isCheckInTab = (value: string | null): value is CheckInTab =>
  value != null && TAB_IDS.includes(value as CheckInTab);

type MyCheckInsProps = {
  defaultTab?: CheckInTab;
};

type SelectedCheckIn = {
  accountId: string;
  cycleId: string;
};

type PortfolioPollGroup = {
  cycle_id: string;
  poll_name: string;
  period: string;
  cycle: {
    id: string;
    name?: string;
    period?: string;
    due_date?: string;
    publication_state?: string;
  };
  accounts: any[];
  summary?: {
    completion?: { done: number; total: number };
  };
};

const emptyAnswers = (): (boolean | null)[] => [null, null, null, null];

const groupCycleId = (group: PortfolioPollGroup): string =>
  group.cycle_id || group.cycle?.id || "";

const isPoll1Complete = (account: any) => Boolean(account.poll1_submitted_at);

const MyCheckIns = ({ defaultTab = "assigned" }: MyCheckInsProps) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  const portfolio = useAppSelector(s => s.sponsorEngagement.portfolio);
  const myPerformance = useAppSelector(s => s.sponsorEngagement.myPerformance);
  const submitting = useAppSelector(
    s => s.sponsorEngagement.submitPoll1.loading
  );
  const savingDraft = useAppSelector(
    s => s.sponsorEngagement.savePoll1Draft.loading
  );

  const activeTab = useMemo<CheckInTab>(
    () => (isCheckInTab(tabFromUrl) ? tabFromUrl : defaultTab),
    [tabFromUrl, defaultTab]
  );
  const [selectedCheckIn, setSelectedCheckIn] =
    useState<SelectedCheckIn | null>(null);
  const selectedCheckInRef = useRef<SelectedCheckIn | null>(null);
  const [submitReveal, setSubmitReveal] = useState<{
    accountId: string;
    cycleId: string;
    score: number;
  } | null>(null);
  const [returnTab, setReturnTab] = useState<CheckInTab>("assigned");
  const [answers, setAnswers] = useState<(boolean | null)[]>(emptyAnswers());
  const [evidence, setEvidence] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [expandedPollIds, setExpandedPollIds] = useState<Set<string>>(
    () => new Set()
  );

  const setSelectedCheckInSafe = (next: SelectedCheckIn | null) => {
    selectedCheckInRef.current = next;
    setSelectedCheckIn(next);
  };

  const resetPoll1Form = () => {
    setAnswers(emptyAnswers());
    setEvidence({});
    setNotes("");
  };

  const portfolioData = portfolio.data;
  const performanceData = myPerformance.data;
  const loading = portfolio.loading || myPerformance.loading;

  const pollGroups: PortfolioPollGroup[] = portfolioData?.poll_groups ?? [];
  const questions: Poll1Question[] = portfolioData?.poll1_questions ?? [];
  const poll1Tiers: Poll1Tier[] = portfolioData?.poll1_tiers ?? [];
  const showRubric = portfolioData?.show_rubric !== false;

  const draftIdsByCycle = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const group of pollGroups) {
      const cycleId = groupCycleId(group);
      if (!cycleId) continue;
      const ids = new Set<string>();
      for (const acct of group.accounts ?? []) {
        if (accountHasPoll1Draft(acct)) {
          ids.add(acct.account_id);
        }
      }
      map.set(cycleId, ids);
    }
    return map;
  }, [pollGroups]);

  const load = useCallback(async () => {
    const [portfolioResult, performanceResult] = await Promise.all([
      dispatch(fetchSponsorEngagementPortfolio()),
      dispatch(fetchSponsorEngagementMyPerformance()),
    ]);
    if (fetchSponsorEngagementPortfolio.rejected.match(portfolioResult)) {
      toast.error(
        (portfolioResult.payload as string) || "Failed to load check-ins"
      );
    }
    if (fetchSponsorEngagementMyPerformance.rejected.match(performanceResult)) {
      toast.error(
        (performanceResult.payload as string) || "Failed to load performance"
      );
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (portfolioData?.poll_groups) {
      const groups: PortfolioPollGroup[] = portfolioData.poll_groups;
      setExpandedPollIds(
        new Set(groups.map(g => groupCycleId(g)).filter(Boolean))
      );
    }
  }, [portfolioData]);

  useEffect(() => {
    if (defaultTab !== "assigned" && !searchParams.get("tab")) {
      setSearchParams({ tab: defaultTab }, { replace: true });
    }
  }, [defaultTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (tabFromUrl === "draft") {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
  }, [tabFromUrl, setSearchParams]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (!isCheckInTab(tabId)) return;
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (tabId === "assigned") {
          next.delete("tab");
        } else {
          next.set("tab", tabId);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const summary = portfolioData?.summary ?? {};
  const period = portfolioData?.period ?? "";
  const completionDone = summary.completion?.done ?? 0;
  const completionTotal = summary.completion?.total ?? 0;

  const tabPartitions = useMemo(() => {
    let assignedPollCount = 0;
    let completedPollCount = 0;
    for (const group of pollGroups) {
      const accounts = group.accounts ?? [];
      const isDraft = (a: { account_id?: string; poll1_has_draft?: boolean }) =>
        accountHasPoll1Draft(a);
      const tab = pollPrimaryTab(accounts, isDraft, isPoll1Complete);
      if (!tab) continue;
      if (tab === "assigned") {
        assignedPollCount += 1;
      } else {
        completedPollCount += 1;
      }
    }
    return { assignedPollCount, completedPollCount };
  }, [pollGroups]);

  const { assignedPollCount, completedPollCount } = tabPartitions;

  const findAccount = useCallback(
    (accountId: string, cycleId: string) => {
      const group = pollGroups.find(g => groupCycleId(g) === cycleId);
      return (group?.accounts ?? []).find(
        (a: any) => a.account_id === accountId
      );
    },
    [pollGroups]
  );

  const applyPoll1DraftToForm = (draft: Poll1Draft) => {
    setAnswers(
      draft.answers.length >= 4
        ? draft.answers.slice(0, 4)
        : [
            ...draft.answers,
            ...Array(Math.max(0, 4 - draft.answers.length)).fill(null),
          ]
    );
    setEvidence(draft.evidence ?? {});
    setNotes(draft.notes ?? "");
  };

  const openPollForm = async (
    accountId: string,
    cycleId: string,
    fromTab: CheckInTab
  ) => {
    setSubmitReveal(null);
    setReturnTab(fromTab);
    const acct = findAccount(accountId, cycleId);

    // Reset/load form before opening so prior answers never flash.
    if (acct?.poll1_submitted_at && Array.isArray(acct.poll1_responses)) {
      const resp = acct.poll1_responses as boolean[];
      setAnswers(
        resp.length >= 4
          ? resp.map(v => (typeof v === "boolean" ? v : null))
          : [
              ...resp.map(v => (typeof v === "boolean" ? v : null)),
              ...Array(Math.max(0, 4 - resp.length)).fill(null),
            ]
      );
      setEvidence((acct.poll1_evidence as Record<string, string>) ?? {});
      setNotes(acct.poll1_notes ?? "");
      setSelectedCheckInSafe({ accountId, cycleId });
      return;
    }

    const embeddedDraft = draftFromAccount(acct);
    if (embeddedDraft) {
      applyPoll1DraftToForm(embeddedDraft);
      setSelectedCheckInSafe({ accountId, cycleId });
      return;
    }

    resetPoll1Form();
    setSelectedCheckInSafe({ accountId, cycleId });

    const draftResult = await dispatch(fetchPoll1Draft({ accountId, cycleId }));
    const current = selectedCheckInRef.current;
    if (
      !current ||
      current.accountId !== accountId ||
      current.cycleId !== cycleId
    ) {
      return;
    }
    if (fetchPoll1Draft.fulfilled.match(draftResult) && draftResult.payload) {
      applyPoll1DraftToForm(draftResult.payload as Poll1Draft);
    } else {
      resetPoll1Form();
    }
  };

  const closePollForm = () => {
    setSelectedCheckInSafe(null);
    setSubmitReveal(null);
    resetPoll1Form();
    handleTabChange(returnTab);
  };

  const saveDraft = async () => {
    if (!selectedCheckIn || savingDraft) return;
    const { accountId, cycleId } = selectedCheckIn;
    if (
      !hasPartialPoll1Answers(answers) &&
      !notes.trim() &&
      !Object.keys(evidence).length
    ) {
      toast.error(
        "Answer at least one question or add notes before saving a draft"
      );
      return;
    }
    const result = await dispatch(
      savePoll1Draft({
        accountId,
        cycleId,
        draft: { answers, notes, evidence },
      })
    );
    if (savePoll1Draft.fulfilled.match(result)) {
      toast.success("Draft saved successfully");
      setSelectedCheckInSafe(null);
      resetPoll1Form();
      await load();
    } else {
      toast.error((result.payload as string) || "Failed to save draft");
    }
  };

  const handleSubmitPoll1 = async (accountId: string, cycleId: string) => {
    if (answers.some(a => a === null)) {
      toast.error("Please answer all questions before submitting");
      return;
    }
    const score = poll1RawFromAnswers(answers, questions);
    const result = await dispatch(
      submitPoll1({
        accountId,
        cycleId,
        payload: {
          answers: answers as boolean[],
          evidence: buildSubmitEvidence(
            answers as boolean[],
            evidence,
            questions
          ),
          notes: notes.trim(),
        },
      })
    );
    if (submitPoll1.fulfilled.match(result)) {
      setSelectedCheckInSafe(null);
      resetPoll1Form();
      setSubmitReveal({ accountId, cycleId, score });
      await load();
    } else {
      toast.error((result.payload as string) || "Submit failed");
    }
  };

  const tabs = useMemo(
    () => [
      {
        id: "assigned",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <Clock className="h-[18px] w-[18px]" size={18} />
            Assigned Polls
          </span>
        ),
        count: assignedPollCount,
      },
      {
        id: "completed",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <CheckCircle className="h-[18px] w-[18px]" size={18} />
            Completed Polls
          </span>
        ),
        count: completedPollCount,
      },
      {
        id: "performance",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <BarChart3 className="h-[18px] w-[18px]" size={18} />
            My Performance
          </span>
        ),
      },
    ],
    [assignedPollCount, completedPollCount]
  );

  const togglePollExpanded = (cycleId: string) => {
    setExpandedPollIds(prev => {
      const next = new Set(prev);
      if (next.has(cycleId)) {
        next.delete(cycleId);
      } else {
        next.add(cycleId);
      }
      return next;
    });
  };

  const renderAccountGrid = (
    rows: any[],
    actionTab: CheckInTab,
    cycleId: string,
    draftIds: Set<string>
  ) => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5">
      {rows.map((row: any) => (
        <AccountCheckInCard
          key={`${cycleId}-${row.account_id}`}
          account={row}
          isDraft={draftIds.has(row.account_id)}
          showStatusBadge="none"
          onAction={() => openPollForm(row.account_id, cycleId, actionTab)}
        />
      ))}
    </div>
  );

  const rowsForTab = (tab: CheckInTab) => (g: PortfolioPollGroup) => {
    const cycleId = groupCycleId(g);
    const draftIds = draftIdsByCycle.get(cycleId) ?? new Set<string>();
    const accounts = g.accounts ?? [];
    const isDraft = (a: any) => draftIds.has(a.account_id ?? "");
    if (pollPrimaryTab(accounts, isDraft, isPoll1Complete) !== tab) {
      return [];
    }
    const rows = accountsForPollTab(accounts, tab, isDraft, isPoll1Complete);
    return [...rows].sort((a, b) =>
      String(a.account_name || "").localeCompare(
        String(b.account_name || ""),
        undefined,
        { sensitivity: "base" }
      )
    );
  };

  const renderPollGroupsForTab = (
    tab: CheckInTab,
    emptyTitle: string,
    emptyHint: string,
    actionTab: CheckInTab
  ) => {
    const pickRows = rowsForTab(tab);
    const visibleGroups = pollGroups.filter(g => {
      const isDraft = (a: { account_id?: string; poll1_has_draft?: boolean }) =>
        accountHasPoll1Draft(a);
      return pollPrimaryTab(g.accounts ?? [], isDraft, isPoll1Complete) === tab;
    });

    if (visibleGroups.length === 0) {
      return (
        <EngagementEmptyPanel
          variant="sponsor"
          icon={CheckCircle}
          title={emptyTitle}
          hint={emptyHint}
        />
      );
    }

    const currentPeriodKey = period?.trim() || "";
    const currentPeriodLabel = currentPeriodKey
      ? formatQuarterPeriodLabel(currentPeriodKey)
      : "";
    const currentGroups = currentPeriodKey
      ? visibleGroups.filter(g => (g.period || "").trim() === currentPeriodKey)
      : visibleGroups;
    const historicalGroups = currentPeriodKey
      ? visibleGroups.filter(g => (g.period || "").trim() !== currentPeriodKey)
      : [];
    const historicalByPeriod = groupPollGroupsByPeriod(historicalGroups);
    const hasHistorical = historicalByPeriod.length > 0;
    const currentEmptyTitle =
      tab === "completed"
        ? `No completed polls for ${currentPeriodLabel || "this quarter"}`
        : `No assigned polls for ${currentPeriodLabel || "this quarter"}`;

    const renderGroupCard = (group: PortfolioPollGroup) => {
      const cycleId = groupCycleId(group);
      const rows = pickRows(group);
      const draftIds = draftIdsByCycle.get(cycleId) ?? new Set<string>();
      const pollName =
        group.poll_name?.trim() ||
        group.cycle?.name?.trim() ||
        (group.period ? `${group.period} Check-In` : "Check-In");
      const groupDone = group.summary?.completion?.done ?? 0;
      const groupTotal = group.summary?.completion?.total ?? 0;

      return (
        <CheckInPollAccordion
          key={cycleId}
          pollName={pollName}
          period={group.period || period}
          cycle={group.cycle}
          accountCount={(group.accounts ?? []).length}
          poll1Done={groupDone}
          poll1Total={groupTotal}
          isExpanded={expandedPollIds.has(cycleId)}
          onToggleExpand={() => togglePollExpanded(cycleId)}
          emptyHint={emptyHint}
          showPublicationBadge={false}
        >
          {renderAccountGrid(rows, actionTab, cycleId, draftIds)}
        </CheckInPollAccordion>
      );
    };

    return (
      <div className="flex flex-col gap-5 mt-1">
        {currentGroups.length > 0 ? (
          <div>
            {hasHistorical && currentPeriodLabel ? (
              <h4 className="mb-3 mt-0 text-sm font-semibold text-gray-800">
                {currentPeriodLabel}
              </h4>
            ) : null}
            <div className="flex flex-col gap-5">
              {currentGroups.map(renderGroupCard)}
            </div>
          </div>
        ) : hasHistorical && tab === "completed" ? (
          <div>
            {currentPeriodLabel ? (
              <h4 className="mb-3 mt-0 text-sm font-semibold text-gray-800">
                {currentPeriodLabel}
              </h4>
            ) : null}
            <EngagementEmptyPanel
              variant="hub-compact"
              icon={List}
              title={currentEmptyTitle}
              hint={emptyHint}
            />
          </div>
        ) : null}

        {hasHistorical ? (
          <div className="flex flex-col gap-3">
            <h4 className="mb-0 mt-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Previous quarters
            </h4>
            {historicalByPeriod.map(({ period: groupPeriod, groups }) => (
              <DiscoveryHubPollSectionAccordion
                key={groupPeriod}
                title={formatQuarterPeriodLabel(groupPeriod)}
                count={groups.length}
                showCountBadge={false}
                collapsible
                defaultExpanded={false}
              >
                <div className="flex flex-col gap-5">
                  {groups.map(renderGroupCard)}
                </div>
              </DiscoveryHubPollSectionAccordion>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const dueDate = portfolioData?.cycle?.due_date;

  const nudgeBanner =
    completionTotal > 0 && completionDone < completionTotal ? (
      <div
        className={`${notificationBannerClass("amber")} justify-between has-[.notif-quarter]:justify-between`}
      >
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <Clock className="shrink-0 mt-px" size={18} />
          <div className="min-w-0">
            <div className="font-bold text-[13px] mb-px">
              Check-In open — {completionDone} of {completionTotal} accounts
              completed
            </div>
            <div className="text-xs opacity-90">
              {dueDate ? `Due ${dueDate}. ` : ""}
              Mid-quarter nudge sent {portfolioData?.nudge_weeks_before ??
                6}{" "}
              weeks before due date.
            </div>
          </div>
        </div>
        {period ? (
          <span className="notif-quarter shrink-0 font-bold text-[13px] whitespace-nowrap ml-3">
            {period}
          </span>
        ) : null}
      </div>
    ) : completionTotal > 0 ? (
      <div className={`${notificationBannerClass("green")} justify-between`}>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <CheckCircle className="shrink-0" size={18} />
          <div className="min-w-0">
            <div className="font-bold text-[13px] mb-px">Check-In complete</div>
            <div className="text-xs opacity-90">
              All {completionTotal} of your accounts submitted. Thank you.
            </div>
          </div>
        </div>
        {period ? (
          <span className="notif-quarter shrink-0 font-bold text-[13px] whitespace-nowrap ml-3">
            {period}
          </span>
        ) : null}
      </div>
    ) : null;

  const revealAccount = submitReveal
    ? findAccount(submitReveal.accountId, submitReveal.cycleId)
    : null;

  const handleRevealReturn = () => {
    if (!submitReveal) return;
    const cycleId = submitReveal.cycleId;
    setSubmitReveal(null);
    const group = pollGroups.find(g => groupCycleId(g) === cycleId);
    const allDone = (group?.accounts ?? []).every(isPoll1Complete);
    handleTabChange(allDone ? "completed" : "assigned");
  };

  const selectedAccount = selectedCheckIn
    ? findAccount(selectedCheckIn.accountId, selectedCheckIn.cycleId)
    : null;

  const selectedGroup = selectedCheckIn
    ? pollGroups.find(g => groupCycleId(g) === selectedCheckIn.cycleId)
    : null;
  const selectedPollName =
    selectedGroup?.poll_name?.trim() ||
    selectedGroup?.cycle?.name?.trim() ||
    undefined;
  const checkInPeriod = selectedGroup?.period || period;
  const revealGroup = submitReveal
    ? pollGroups.find(g => groupCycleId(g) === submitReveal.cycleId)
    : null;
  const revealPeriod = revealGroup?.period || period;

  const hasActivePolls = pollGroups.length > 0;

  const showSamplePreview = useMemo(() => {
    if (loading) return false;
    const isDraftAcct = (a: {
      account_id?: string;
      poll1_has_draft?: boolean;
    }) => accountHasPoll1Draft(a);
    const assignedHasData = pollGroups.some(
      g =>
        pollPrimaryTab(g.accounts ?? [], isDraftAcct, isPoll1Complete) ===
        "assigned"
    );
    const completedHasData = pollGroups.some(
      g =>
        pollPrimaryTab(g.accounts ?? [], isDraftAcct, isPoll1Complete) ===
        "completed"
    );
    const performanceHasData =
      Boolean(performanceData?.cycle) &&
      (performanceData?.accounts?.length ?? 0) > 0;
    return !assignedHasData && !completedHasData && !performanceHasData;
  }, [loading, pollGroups, performanceData]);

  return (
    <div className="min-h-full flex flex-col bg-white mt-3">
      <PageHeader title="My Check-Ins" icon={CircleCheckBig} />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4 flex flex-col gap-5 min-h-[calc(100vh-220px)]">
          <div className="w-full min-w-0 overflow-x-auto overflow-y-visible [-webkit-overflow-scrolling:touch]  bg-white p-3">
            <TabsComponent
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              variant="tab-segmented"
            />
          </div>

          <div className="flex flex-col gap-5">
            {loading ? (
              <HubPageLoading />
            ) : activeTab === "performance" ? (
              <MyCheckInsPerformancePanel data={performanceData} />
            ) : showSamplePreview ? (
              <SampleDataOverlay message={MCI_SAMPLE_MESSAGES[activeTab]}>
                {activeTab === "assigned" ? (
                  <SampleMyCheckInsAssigned />
                ) : (
                  <SampleMyCheckInsCompleted />
                )}
              </SampleDataOverlay>
            ) : !hasActivePolls ? (
              <EngagementEmptyPanel
                variant="sponsor"
                icon={CircleCheckBig}
                title="No check-ins yet"
                hint="Your program lead will launch the quarterly poll from ExecOps Discovery Hub. Assigned and completed polls from all quarters will appear here."
              />
            ) : (
              <>
                {nudgeBanner}
                {activeTab === "assigned" &&
                  renderPollGroupsForTab(
                    "assigned",
                    "No assigned polls",
                    "All accounts are completed for these polls.",
                    "assigned"
                  )}
                {activeTab === "completed" &&
                  renderPollGroupsForTab(
                    "completed",
                    "No completed polls",
                    "Polls appear here after every assigned account has submitted its check-in.",
                    "completed"
                  )}
              </>
            )}
          </div>
        </div>
      </div>

      <CheckInModal
        isOpen={Boolean(selectedCheckIn)}
        onClose={closePollForm}
        account={selectedAccount}
        period={checkInPeriod}
        cycleName={selectedPollName}
        questions={questions}
        poll1Tiers={poll1Tiers}
        showRubric={showRubric}
        answers={answers}
        evidence={evidence}
        notes={notes}
        onAnswersChange={setAnswers}
        onEvidenceChange={setEvidence}
        onNotesChange={setNotes}
        onSaveDraft={saveDraft}
        onSubmit={() => {
          if (selectedCheckIn) {
            handleSubmitPoll1(
              selectedCheckIn.accountId,
              selectedCheckIn.cycleId
            );
          }
        }}
        submitting={submitting}
        savingDraft={savingDraft}
      />

      <ScoreRevealPanel
        isOpen={Boolean(submitReveal && revealAccount)}
        accountName={revealAccount?.account_name ?? ""}
        period={revealPeriod}
        score={submitReveal?.score ?? 0}
        poll1Tiers={poll1Tiers}
        onReturn={handleRevealReturn}
      />
    </div>
  );
};

export default MyCheckIns;
